import type { DashboardActivity, DashboardMetric } from '@flux/shared';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { StatTile } from './stat-tile.js';

/** Répartition par origine, affichée en infobulle sur chaque tuile d'activité. */
function OriginBreakdown({ metric }: { metric: DashboardMetric }): JSX.Element {
  const entries = Object.entries(metric.byOrigin).filter(([, count]) => count > 0);
  if (entries.length === 0) {
    return <span>Aucune</span>;
  }
  return (
    <ul className="flex flex-col gap-0.5 tabular-nums">
      {entries.map(([origin, count]) => (
        <li key={origin}>
          {origin === 'pennylane' ? 'Pennylane' : origin.toUpperCase()} : {count}
        </li>
      ))}
    </ul>
  );
}

export interface ActivityRowProps {
  activity: DashboardActivity;
  periodLabel?: string;
}

/**
 * Ligne « Activité » (`02` §2) : reçues / transmises / expédiées, aujourd'hui
 * et sur 7 jours. Un seul panneau divisé en colonnes — des indicateurs
 * compacts, pas des cartes. La répartition par origine vit en infobulle :
 * c'est un détail, plus l'unité de lecture.
 */
export function ActivityRow({ activity, periodLabel = 'aujourd’hui' }: ActivityRowProps): JSX.Element {
  const selected = activity.selected ?? activity.today;
  const tiles: { label: string; selected: DashboardMetric }[] = [
    { label: 'Reçues', selected: selected.received },
    {
      label: 'Transmises',
      selected: selected.transmitted,
    },
    { label: 'Expédiées', selected: selected.shipped },
  ];

  return (
    <PageSection title="Activité" variant="plain">
      <div className="grid divide-y divide-border rounded-lg border border-border bg-card sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {tiles.map((tile) => (
          <StatTile
            key={tile.label}
            label={tile.label}
            value={tile.selected.total}
            hint={periodLabel}
            tooltip={<OriginBreakdown metric={tile.selected} />}
          />
        ))}
      </div>
    </PageSection>
  );
}
